import styled from 'styled-components';
import {
  BarChart,
  Bar,
  XAxis,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from 'recharts';
import { formatCurrency, formatToEokMan } from '../../../../func/Function';
import { useEffect, useMemo, useState } from 'react';
import UnitSwitch from '../../../../func/components/UnitSwitch';
import Tab from './Tab';
import {
  AroundRealDealFilter,
  priceAnalysisProps,
  RealDealData,
  RealDealDataItem,
  RealDealFilter,
} from '../../data';
// import NoDataMessage from '../../components/NoData';
import { useReportContext } from '../../../../context/DetailContext';
import Pagination from '../../../components/pagination/Pagination';
import { userGradeType } from '../../../../utils/auth';
import PriceAnalysisLimitBox from './PriceAnalysisLimitBox';

interface DummyData {
  name: string;
  price: number;
}

interface PriceAnalysisProps {
  realPriceData?: RealDealData; //동일물건 실거래가
  nearRealPriceData?: RealDealData; // 주변실거래가
  priceAnalysisData: priceAnalysisProps; //시세분석
  realDealFilter: RealDealFilter;
  setRealDealFilter: any;
  aroundRealDealFilter: AroundRealDealFilter;
  setAroundRealDealFilter: any;
  sameItemAddr: string;
  isExample?: boolean;
  linkSet?: any;
  caseInfo?: any;
}

const PriceAnalysis = ({
  realPriceData,
  nearRealPriceData,
  priceAnalysisData,
  realDealFilter,
  setRealDealFilter,
  aroundRealDealFilter,
  setAroundRealDealFilter,
  sameItemAddr,
  isExample,
  linkSet,
  caseInfo
}: PriceAnalysisProps) => {
  const [unit, setUnit] = useState('평');
  const [unit2, setUnit2] = useState('평');
  const [actualPriceTab, setActualPriceTab] = useState('매매');
  const [nearPriceTab, setNearPriceTab] = useState('매매');
  const { itemType, zoning, type, exchangeItemCategory } = useReportContext();
  const [realPriceList, setRealPriceList] = useState<RealDealDataItem[]>([]);
  const [nearRealPriceList, setNearRealPriceList] = useState<
    RealDealDataItem[]
  >([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentPage2, setCurrentPage2] = useState<number>(1);
  const [onSameArea, setOnSameArea] = useState(false);
  const [onSameFloor, setOnSameFloor] = useState(false);
  const [onNearArea, setOnNearArea] = useState(false);
  const [onNearFloor, setOnNearFloor] = useState(false);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);

    setRealDealFilter({ ...realDealFilter, offset: (page - 1) * 6 });
  };
  const handlePageChange2 = (page: number) => {
    setCurrentPage2(page);

    setAroundRealDealFilter({
      ...aroundRealDealFilter,
      offset: (page - 1) * 6,
    });
  };

  useEffect(() => {
    if (realPriceData) {
      switch (actualPriceTab) {
        case '매매':
          setRealPriceList(realPriceData.sale);
          break;
        case '전˙월세':
          setRealPriceList(realPriceData.jeonse);

          break;
        default:
          setRealPriceList(realPriceData.sale);
          break;
      }
    }
  }, [actualPriceTab, realPriceData, realDealFilter]);

  useEffect(() => {
    if (nearRealPriceData) {
      switch (nearPriceTab) {
        case '매매':
          setNearRealPriceList(nearRealPriceData.sale);
          break;
        case '전˙월세':
          setNearRealPriceList(nearRealPriceData.jeonse);
          break;
        default:
          setNearRealPriceList(nearRealPriceData.sale);
          break;
      }
    }
  }, [nearPriceTab, nearRealPriceData, aroundRealDealFilter]);

  const chartDataSet: DummyData[] = [
    {
      name: '감정가',
      price: Number(priceAnalysisData.appraisedPrc_meter),
    },
    {
      name: window.location.pathname.includes('/exchange/detail')
        ? '희망가'
        : '최저입찰가',
      price: Number(priceAnalysisData.minPrc_meter),
    },
    {
      name: '상위평균가',
      price: priceAnalysisData.avgPrc_high_meter,
    },
    {
      name: '중위평균가',
      price: priceAnalysisData.avgPrc_meter,
    },
    {
      name: '하위평균가',
      price: priceAnalysisData.avgPrc_low_meter,
    },
  ];

  const [userGrade, setUserGrade] = useState<string | null>(null);

  useEffect(() => {
    if (isExample) return;
    const fetchUserGrade = async () => {
      try {
        const grade = await userGradeType();
        setUserGrade(grade);
      } catch (error) {
        console.error('유저 등급 정보 가져오기 실패:', error);
      }
    };

    fetchUserGrade();
  }, []);

  const getBarColor = (name: string) => {
    switch (name) {
      case '감정가':
        return '#0052BD';
      case '최저입찰가':
        return '#FA4032';
      case '희망가':
        return '#FA4032';
      case '중위평균가':
        return '#008AFF';
      default:
        return '#E8E9EA';
    }
  };

  const medianValue =
    chartDataSet.find((item) => item.name === '중위평균가')?.price || 0;

  return (
    <Container id="가격분석">
      <Title>가격분석</Title>
      {/* Todo: 지역 멤버십 연결 */}
      {/* <div style={{ marginBottom: '60px' }}>
        <ContentItemTitle>
          <img src="/assets/images/icon/Icon_chart.svg"></img>
          시세분석
        </ContentItemTitle>
        <PriceAnalysisLimitBox isRegion />
      </div> */}
      {!isExample && !userGrade ? (
        <div style={{ marginBottom: '60px' }}>
          <ContentItemTitle>
            <img src="/assets/images/icon/Icon_chart.svg"></img>
            시세분석
          </ContentItemTitle>
          <PriceAnalysisLimitBox />
        </div>
      ) : (
        <Content>
          <ContentItem>
            <ContentItemTitle>
              <img src="/assets/images/icon/Icon_chart.svg"></img>
              시세분석
            </ContentItemTitle>
            <div style={{ width: '100%', height: '200px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartDataSet}
                  margin={{ top: 30, right: 30, left: 20, bottom: 5 }}
                >
                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    interval={0}
                    tick={({ x, y, payload }) => {
                      const color =
                        payload.value === '상위평균가' ||
                        payload.value === '하위평균가'
                          ? 'rgba(23, 30, 38, 0.40)'
                          : '#171E26';

                      return (
                        <text
                          x={x}
                          y={y + 10}
                          fill={color}
                          fontSize="11px"
                          fontWeight="500"
                          fontFamily="'Spoqa Han Sans Neo', sans-serif"
                          textAnchor="middle"
                        >
                          {payload.value}
                        </text>
                      );
                    }}
                  />

                  <Bar
                    dataKey="price"
                    fill="#82ca9d"
                    barSize={40}
                    radius={4}
                    label={({ x, y, value }) => (
                      <text
                        x={x! + 20}
                        y={y! - 10}
                        fill="#333"
                        fontSize="14px"
                        fontWeight="700"
                        fontFamily="Spoqa Han Sans Neo"
                        textAnchor="middle"
                      >
                        {formatCurrency(value)}
                      </text>
                    )}
                  >
                    {chartDataSet.map((entry) => (
                      <Cell key={entry.name} fill={getBarColor(entry.name)} />
                    ))}
                  </Bar>
                  <ReferenceLine
                    y={medianValue}
                    stroke="rgba(23, 30, 38, 0.40)"
                    strokeDasharray="3 3"
                    label={{
                      position: 'insideRight',
                      value: '평균',
                      fontSize: 12,
                      fontFamily: 'Spoqa Han Sans Neo',
                      fontWeight: 500,
                      dx: 30,
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </ContentItem>
          {
            <ContentItem>
              <ContentItemTitle>
                <TitleWrapper>
                  <TitleBox>
                    <TitleContent>
                      <img src="/assets/images/icon/icon_sameAddress.svg"></img>
                      <div>동일 물건 실거래가</div>
                    </TitleContent>
                    <UnitSwitch unit={unit} setUnit={setUnit} />
                  </TitleBox>
                  <div>
                    {sameItemAddr ? <BlueTag>{sameItemAddr}</BlueTag> : null}
                  </div>
                  {/* Todo: 토글 버튼 */}
                  {/* <FilterBox>
                    <ToggleBox>
                      <ToggleText>동일면적 찾기</ToggleText>
                      <Toggle
                        isOn={onSameArea}
                        onClick={() => setOnSameArea((prev) => !prev)}
                      >
                        <ToggleCircle isOn={onSameArea} />
                      </Toggle>
                    </ToggleBox>
                    <ToggleBox>
                      <ToggleText>동일층수 찾기</ToggleText>
                      <Toggle
                        isOn={onSameFloor}
                        onClick={() => setOnSameFloor((prev) => !prev)}
                      >
                        <ToggleCircle isOn={onSameFloor} />
                      </Toggle>
                    </ToggleBox>
                  </FilterBox> */}
                </TitleWrapper>
              </ContentItemTitle>
              <Tab
                tab={actualPriceTab}
                setTab={setActualPriceTab}
                setCurrentPage={setCurrentPage}
                isOnlySale={
                  !(type === 'e'
                    ? exchangeItemCategory
                    : (realPriceData?.ptype ?? '')) ||
                  ['단독', '빌딩', '공장', '창고', '토지'].includes(
                    type === 'e'
                      ? exchangeItemCategory
                      : (realPriceData?.ptype ?? ''),
                  ) ||
                  itemType === 'L'
                }
              />
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderTop: '1px solid #222',
                }}
              >
                {!(type === 'e'
                  ? exchangeItemCategory
                  : (realPriceData?.ptype ?? '')) ||
                ['단독', '빌딩', '공장', '창고', '토지'].includes(
                  type === 'e'
                    ? exchangeItemCategory
                    : (realPriceData?.ptype ?? ''),
                ) ||
                itemType === 'L' ? (
                  <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                    <thead style={{ width: '100%' }}>
                      <tr>
                        <TableHeadTh width="16%" rowSpan={1}>
                          거래일
                        </TableHeadTh>
                        <TableHeadTh width="42%" colSpan={1}>
                          금액(단가 평/m²)
                        </TableHeadTh>
                        <TableHeadTh width="42%" rowSpan={1}>
                          면적({unit === '평' ? '평' : 'm²'}) <br />
                        </TableHeadTh>
                      </tr>
                    </thead>
                    <tbody style={{ width: '100%' }}>
                      {realPriceList.length > 0 &&
                        actualPriceTab === '매매' &&
                        realPriceList.map((data, index) => (
                          <tr
                            style={{ borderBottom: '1px solid #e8e9ea' }}
                            key={index}
                          >
                            <TablebodyTd width="16%">
                              {data.dealdate.split('T')[0]}
                            </TablebodyTd>
                            <TablebodyTd
                              width="42%"
                              style={{ textAlign: 'end' }}
                            >
                              {formatToEokMan(data.dealprice)}
                              <br></br>(
                              {unit === '평'
                                ? formatToEokMan(data.price_per_pyeong)
                                : formatToEokMan(
                                    data.price_per_pyeong / 3.3058,
                                  )}
                              / {unit === '평' ? '평' : 'm²'})
                            </TablebodyTd>
                            <TablebodyTd width="42%">
                              {unit === '평'
                                ? data.pyeong
                                : (data.pyeong * 3.3058).toFixed(1)}
                              {unit === '평' ? '평' : 'm²'}
                            </TablebodyTd>
                          </tr>
                        ))}
                      {realPriceList.length === 0 && NoDataMessage()}
                    </tbody>
                  </table>
                ) : (
                  <table
                    style={{
                      borderCollapse: 'collapse',
                      width: '100%',
                    }}
                  >
                    <thead style={{ width: '100%' }}>
                      <tr>
                        <TableHeadTh width="16%" rowSpan={1}>
                          거래일
                        </TableHeadTh>
                        <TableHeadTh width="42%" colSpan={1}>
                          금액(단가 평/m²)
                        </TableHeadTh>
                        <TableHeadTh width="42%" rowSpan={1}>
                          면적({unit === '평' ? '평' : 'm²'}) <br />
                          (층)
                        </TableHeadTh>
                      </tr>
                    </thead>
                    <tbody style={{ width: '100%' }}>
                      {realPriceList.length > 0 &&
                        actualPriceTab === '매매' &&
                        realPriceList.map((data, index) => (
                          <tr
                            style={{ borderBottom: '1px solid #e8e9ea' }}
                            key={index}
                          >
                            <TablebodyTd width="16%">
                              {data.dealdate.split('T')[0]}
                            </TablebodyTd>
                            <TablebodyTd
                              width="42%"
                              style={{ textAlign: 'end' }}
                            >
                              {formatToEokMan(data.dealprice)}
                              <br></br>(
                              {unit === '평'
                                ? formatToEokMan(data.price_per_pyeong)
                                : formatToEokMan(
                                    data.price_per_pyeong / 3.3058,
                                  )}
                              / {unit === '평' ? '평' : 'm²'})
                            </TablebodyTd>
                            <TablebodyTd width="42%">
                              {unit === '평'
                                ? data.pyeong
                                : (data.pyeong * 3.3058).toFixed(1)}
                              {unit === '평' ? '평' : 'm²'}
                              <br />(
                              {data.floor > 0
                                ? data.floor
                                : `B${Math.abs(data.floor)}`}
                              층)
                            </TablebodyTd>
                          </tr>
                        ))}
                      {realPriceList.length > 0 &&
                        actualPriceTab === '전˙월세' &&
                        realPriceList.map((data, index) => (
                          <tr
                            style={{ borderBottom: '1px solid #e8e9ea' }}
                            key={index}
                          >
                            <TablebodyTd width="16%">
                              {data.dealdate.split('T')[0]}
                            </TablebodyTd>
                            <TablebodyTd width="42%">
                              <PriceTagBox>
                                {data.dealprice2 !== 0 ? (
                                  <PriceTagRow>
                                    <span>
                                      {formatToEokMan(data.dealprice)}
                                    </span>
                                    /
                                    <span>
                                      {formatToEokMan(data.dealprice2)}
                                    </span>
                                  </PriceTagRow>
                                ) : (
                                  <PriceTagRow>
                                    <span>
                                      {formatToEokMan(data.dealprice)}
                                    </span>
                                  </PriceTagRow>
                                )}
                                {/* <br></br> */}
                                <div>
                                  {unit === '평'
                                    ? formatToEokMan(data.price_per_pyeong)
                                    : formatToEokMan(
                                        data.price_per_pyeong / 3.3058,
                                      )}
                                  / {unit === '평' ? '평' : 'm²'}
                                </div>
                              </PriceTagBox>
                            </TablebodyTd>
                            <TablebodyTd width="42%">
                              {unit === '평'
                                ? data.pyeong
                                : (data.pyeong * 3.3058).toFixed(1)}
                              {unit === '평' ? '평' : 'm²'}
                              <br></br>(
                              {data.floor > 0
                                ? data.floor
                                : `B${Math.abs(data.floor)}`}
                              층)
                            </TablebodyTd>
                          </tr>
                        ))}
                      {realPriceList.length === 0 && NoDataMessage()}
                    </tbody>
                  </table>
                )}
              </div>
              {realPriceList.length > 0 && (
                <Pagination
                  removeMargin={true}
                  currentPage={currentPage}
                  totalPages={Math.ceil(
                    Number(
                      actualPriceTab === '매매'
                        ? (realPriceData?.sale_total ?? 0)
                        : Number(realPriceData?.jeonse_total ?? 0) +
                            Number(realPriceData?.monthly_total ?? 0),
                    ) / 6,
                  )}
                  onPageChange={handlePageChange}
                />
              )}
            </ContentItem>
          }
          {
            <ContentItem>
              <ContentItemTitle>
                <TitleWrapper>
                  <TitleBox>
                    <TitleContent>
                      <img src="/assets/images/icon/icon_sameAddress.svg"></img>
                      <div>인근 물건 실거래가</div>
                    </TitleContent>
                    <UnitSwitch unit={unit2} setUnit={setUnit2} />
                  </TitleBox>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: '6px',
                    }}
                  >
                    {type === 'e' ? (
                      <BlueTag>{exchangeItemCategory}</BlueTag>
                    ) : nearRealPriceData?.ptype ? (
                      <>
                        <BlueTag>{nearRealPriceData?.ptype}</BlueTag>
                        {!['토지', '주차장', '빌딩', '기타'].includes(
                          nearRealPriceData?.ptype ?? '',
                        ) && <BlueTag>{nearRealPriceData?.ptype2}</BlueTag>}
                        {nearRealPriceData?.ptype === '토지' && (
                          <BlueTag>{zoning}</BlueTag>
                        )}
                      </>
                    ) : null}
                    <BlueTag>
                      반경{' '}
                      {aroundRealDealFilter.distance === 500
                        ? '500m'
                        : aroundRealDealFilter.distance % 1000 === 0
                          ? `${aroundRealDealFilter.distance / 1000}km`
                          : `${(aroundRealDealFilter.distance / 1000).toFixed(1)}km`}
                    </BlueTag>
                  </div>
                  {/* Todo: Toggle버튼 */}
                  {/* <FilterBox>
                    <ToggleBox>
                      <ToggleText>동일면적 찾기</ToggleText>
                      <Toggle
                        isOn={onNearArea}
                        onClick={() => setOnNearArea((prev) => !prev)}
                      >
                        <ToggleCircle isOn={onNearArea} />
                      </Toggle>
                    </ToggleBox>
                    <ToggleBox>
                      <ToggleText>동일층수 찾기</ToggleText>
                      <Toggle
                        isOn={onNearFloor}
                        onClick={() => setOnNearFloor((prev) => !prev)}
                      >
                        <ToggleCircle isOn={onNearFloor} />
                      </Toggle>
                    </ToggleBox>
                  </FilterBox> */}
                  <DistanceWrapper>
                    <DistanceBox>
                      <DistanceButton
                        onClick={() =>
                          setAroundRealDealFilter({
                            ...aroundRealDealFilter,
                            distance: 500,
                          })
                        }
                        selectYN={aroundRealDealFilter.distance === 500}
                      >
                        500m
                      </DistanceButton>
                      <DistanceButton
                        onClick={() =>
                          setAroundRealDealFilter({
                            ...aroundRealDealFilter,
                            distance: 1000,
                          })
                        }
                        selectYN={aroundRealDealFilter.distance === 1000}
                      >
                        1km
                      </DistanceButton>
                      <DistanceButton
                        onClick={() =>
                          setAroundRealDealFilter({
                            ...aroundRealDealFilter,
                            distance: 1500,
                          })
                        }
                        selectYN={aroundRealDealFilter.distance === 1500}
                      >
                        1.5km
                      </DistanceButton>
                      <DistanceButton
                        onClick={() =>
                          setAroundRealDealFilter({
                            ...aroundRealDealFilter,
                            distance: 2000,
                          })
                        }
                        selectYN={aroundRealDealFilter.distance === 2000}
                      >
                        2km
                      </DistanceButton>
                    </DistanceBox>
                  </DistanceWrapper>
                </TitleWrapper>
              </ContentItemTitle>
              <Tab
                tab={nearPriceTab}
                setTab={setNearPriceTab}
                setCurrentPage={setCurrentPage2}
                isOnlySale={
                  !(type === 'e'
                    ? exchangeItemCategory
                    : nearRealPriceData?.ptype) ||
                  ['단독', '빌딩', '공장', '창고', '토지'].includes(
                    type === 'e'
                      ? exchangeItemCategory
                      : (nearRealPriceData?.ptype ?? ''),
                  ) ||
                  itemType === 'L'
                }
              />
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderTop: '1px solid #222',
                }}
              >
                {!(type === 'e'
                  ? exchangeItemCategory
                  : nearRealPriceData?.ptype) ||
                ['단독', '빌딩', '공장', '창고', '토지'].includes(
                  type === 'e'
                    ? exchangeItemCategory
                    : (nearRealPriceData?.ptype ?? ''),
                ) ||
                itemType === 'L' ? (
                  <table style={{ borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <TableHeadTh width="16%" rowSpan={1}>
                          거래일
                        </TableHeadTh>
                        <TableHeadTh width="20%" rowSpan={1}>
                          주소(반경)
                        </TableHeadTh>
                        <TableHeadTh width="32%" colSpan={1}>
                          금액(단가 {unit2 === '평' ? '평' : 'm²'}/m²)
                        </TableHeadTh>
                        <TableHeadTh width="32%" rowSpan={1}>
                          면적(평/m²)
                        </TableHeadTh>
                      </tr>
                    </thead>
                    <tbody>
                      {nearRealPriceList.length > 0 &&
                        nearPriceTab === '매매' &&
                        nearRealPriceList.map((data, index) => (
                          <tr
                            style={{ borderBottom: '1px solid #e8e9ea' }}
                            key={index}
                          >
                            <TablebodyTd width="16%">
                              {data.dealdate.split('T')[0]}
                            </TablebodyTd>
                            <TablebodyTd width="20%">
                              {data.region ?? data.legaldong ?? '-'}
                              <br></br>
                              (인근 {data.distance.toLocaleString()}m)
                            </TablebodyTd>
                            <TablebodyTd width="32%">
                              {formatToEokMan(data.dealprice)}
                              <br></br>(
                              {unit2 === '평'
                                ? formatToEokMan(data.price_per_pyeong)
                                : formatToEokMan(
                                    data.price_per_pyeong / 3.3058,
                                  )}
                              / {unit2 === '평' ? '평' : 'm²'})
                            </TablebodyTd>
                            <TablebodyTd width="32%">
                              {unit2 === '평'
                                ? data.pyeong
                                : (data.pyeong * 3.3058).toFixed(1)}
                              {unit2 === '평' ? '평' : 'm²'}
                            </TablebodyTd>
                          </tr>
                        ))}
                      {nearRealPriceList.length === 0 && NoDataMessage()}
                    </tbody>
                  </table>
                ) : (
                  <table
                    style={{
                      borderCollapse: 'collapse',
                      width: '100%',
                    }}
                  >
                    <thead style={{ width: '100%' }}>
                      <tr>
                        <TableHeadTh width="16%" rowSpan={1}>
                          거래일
                        </TableHeadTh>
                        <TableHeadTh width="20%" rowSpan={1}>
                          주소(반경)
                        </TableHeadTh>
                        <TableHeadTh width="32%" colSpan={1}>
                          금액(단가 평/m²)
                        </TableHeadTh>
                        <TableHeadTh width="32%" rowSpan={1}>
                          면적(평/m²) <br />
                          (층)
                        </TableHeadTh>
                      </tr>
                    </thead>
                    <tbody style={{ width: '100%' }}>
                      {nearRealPriceList.length > 0 &&
                        nearPriceTab === '매매' &&
                        nearRealPriceList.map((data, index) => (
                          <tr
                            style={{ borderBottom: '1px solid #e8e9ea' }}
                            key={index}
                          >
                            <TablebodyTd width="16%">
                              {data.dealdate.split('T')[0]}
                            </TablebodyTd>
                            <TablebodyTd width="20%">
                              {data.region}
                              <br></br>({data.distance}m)
                            </TablebodyTd>

                            <TablebodyTd
                              width="44%"
                              style={{ textAlign: 'end' }}
                            >
                              {formatToEokMan(data.dealprice)}
                              <br></br>(
                              {unit2 === '평'
                                ? formatToEokMan(data.price_per_pyeong)
                                : formatToEokMan(
                                    data.price_per_pyeong / 3.3058,
                                  )}
                              / {unit2 === '평' ? '평' : 'm²'})
                            </TablebodyTd>
                            <TablebodyTd width="20%">
                              {unit2 === '평'
                                ? data.pyeong
                                : (data.pyeong * 3.3058).toFixed(1)}
                              {unit2 === '평' ? '평' : 'm²'}
                              <br />(
                              {data.floor > 0
                                ? data.floor
                                : `B${Math.abs(data.floor)}`}
                              층)
                            </TablebodyTd>
                          </tr>
                        ))}
                      {nearRealPriceList.length > 0 &&
                        nearPriceTab === '전˙월세' &&
                        nearRealPriceList.map((data, index) => (
                          <tr
                            style={{ borderBottom: '1px solid #e8e9ea' }}
                            key={index}
                          >
                            <TablebodyTd width="16%">
                              {data.dealdate.split('T')[0]}
                            </TablebodyTd>
                            <TablebodyTd width="20%">
                              {data.region}
                              <br></br>({data.distance}m)
                            </TablebodyTd>
                            <TablebodyTd width="44%">
                              <PriceTagBox>
                                {data.dealprice2 !== 0 ? (
                                  <PriceTagRow>
                                    <span>
                                      {formatToEokMan(data.dealprice)}
                                    </span>
                                    /
                                    <span>
                                      {formatToEokMan(data.dealprice2)}
                                    </span>
                                  </PriceTagRow>
                                ) : (
                                  <PriceTagRow>
                                    <span>
                                      {formatToEokMan(data.dealprice)}
                                    </span>
                                  </PriceTagRow>
                                )}
                                {/* <br></br> */}
                                <div>
                                  {unit2 === '평'
                                    ? formatToEokMan(data.price_per_pyeong)
                                    : formatToEokMan(
                                        data.price_per_pyeong / 3.3058,
                                      )}
                                  / {unit2 === '평' ? '평' : 'm²'}
                                </div>
                              </PriceTagBox>
                            </TablebodyTd>
                            <TablebodyTd width="20%">
                              {unit2 === '평'
                                ? data.pyeong
                                : (data.pyeong * 3.3058).toFixed(1)}
                              {unit2 === '평' ? '평' : 'm²'}
                              <br></br>(
                              {data.floor > 0
                                ? data.floor
                                : `B${Math.abs(data.floor)}`}
                              층)
                            </TablebodyTd>
                          </tr>
                        ))}
                      {nearRealPriceList.length === 0 && NoDataMessage()}
                    </tbody>
                  </table>
                )}
              </div>
              {nearRealPriceList.length > 0 && (
                <Pagination
                  removeMargin={true}
                  currentPage={currentPage2}
                  totalPages={Math.ceil(
                    Number(
                      nearPriceTab === '매매'
                        ? (nearRealPriceData?.sale_total ?? 0)
                        : Number(nearRealPriceData?.jeonse_total ?? 0) +
                            Number(nearRealPriceData?.monthly_total ?? 0),
                    ) / 6,
                  )}
                  onPageChange={handlePageChange2}
                />
              )}
            </ContentItem>
          }
        </Content>
      )}
    </Container>
  );
};

export default PriceAnalysis;

function NoDataMessage() {
  return (
    <tr style={{ borderBottom: '1px solid #e8e9ea' }}>
      <TablebodyTd width="100%" colSpan={4}>
        최근 10년 실거래 이력이 없습니다.
      </TablebodyTd>
    </tr>
  );
}

const TitleBox = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const TitleWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 8px;
`;

interface WidthProps {
  width: string;
}

interface DistanceProps {
  selectYN: boolean;
}

const TitleContent = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  width: 100%;
`;

const DistanceButton = styled.div<DistanceProps>`
  padding: 6px 16px;
  border-radius: 9999px;
  background: ${({ selectYN }) =>
    selectYN ? 'white' : 'rgba(112, 115, 124, 0.08)'};
  color: ${({ selectYN }) => (selectYN ? '#008AFF' : '#989BA2')};
  border: ${({ selectYN }) => (selectYN ? '1px solid #008AFF' : 'none')};
  font-family: Spoqa Han Sans Neo;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
`;

const DistanceBox = styled.div`
  display: flex;
  flex-direction: row;
  // background-color: rgba(112, 115, 124, 0.08);
  border-radius: 4px;
  padding: 4px;
  gap: 8px;
`;

const DistanceWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  color: #222222;
  font-size: 14px;
  font-family: 'Spoqa Han Sans Neo';
  font-weight: 500;
  line-height: 20.3px;
  word-wrap: break-word;
`;

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 32px 16px;
  background: white;
`;

const Title = styled.div`
  width: 100%;
  font-family: Spoqa Han Sans Neo;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 26.1px;
  letter-spacing: -0.44px;
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: flex-end;
`;

const Content = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ContentItem = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const ContentItemTitle = styled.div`
  font-family: Spoqa Han Sans Neo;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 23.2px;
  letter-spacing: -0.36px;
  color: #171e26;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  gap: 4px;
  align-items: center;
  // div {
  //   display: flex;
  //   flex-direction: row;
  //   align-items: center;
  //   gap: 8px;
  // }
`;
const TableHeadTh = styled.th<WidthProps>`
  width: ${({ width }) => width};
  text-align: center;
  border-right: 1px solid #e8e9ea;
  padding: 10px 4px;
  background-color: #f7f8fc;
  font-family: Spoqa Han Sans Neo;
  font-size: 13px;
  font-style: normal;
  font-weight: 500;
  line-height: 18.85px;
  letter-spacing: -0.36px;
  color: #616161;
  &:last-child {
    border-right: none;
  }
`;
const TablebodyTd = styled.td<WidthProps>`
  width: ${({ width }) => width};
  text-align: center;
  border-right: 1px solid #e8e9ea;
  padding: 10px 4px;
  font-family: Spoqa Han Sans Neo;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20.3px;
  letter-spacing: -0.32px;
  color: #222222;

  &:last-child {
    border-right: none;
  }
`;

const BlueTag = styled.span`
  padding: 3px 8px;
  border-radius: 4px;
  background: #f2f7ff;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  font-family: Spoqa Han Sans Neo;
  font-size: 11px;
  font-style: normal;
  font-weight: 500;
  line-height: 15.95px;
  letter-spacing: -0.28px;
  color: #008aff;
`;

const PriceTagBox = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-end;
  // gap: 6px;
`;
const PriceTagRow = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: flex-end;
  // padding: 0 6px;
`;
const DepositTag = styled.span`
  width: 25px;
  height: 25px;
  border-radius: 4px;
  background: #008aff;
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: Spoqa Han Sans Neo;
  font-size: 14px;
  font-weight: 500;
  color: white;
`;
const MonthlyRentTag = styled.span`
  width: 25px;
  height: 25px;
  border-radius: 4px;
  background: #fa652f;
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: Spoqa Han Sans Neo;
  font-size: 14px;
  font-weight: 500;
  color: white;
`;

const FilterBox = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-radius: 8px;
  padding: 8px;
  border: 1px solid #e5e8ea;
`;

const ToggleBox = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;
const ToggleText = styled.div`
  font-family: Spoqa Han Sans Neo;
  font-weight: 500;
  font-size: 13px;
  color: #616161;
`;

const Toggle = styled.div<{ isOn: boolean }>`
  width: 42px;
  height: 22px;
  border-radius: 999px;
  background-color: ${({ isOn }) => (isOn ? '#008AFF' : '#37383C29')};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const ToggleCircle = styled.div<{ isOn: boolean }>`
  width: 18px;
  height: 18px;
  background-color: #fff;
  border-radius: 50%;
  transition: transform 0.3s ease;
  transform: ${({ isOn }) => (isOn ? 'translateX(10px)' : 'translateX(-10px)')};
`;
